import { SubCard } from './chunk-W2MDQRQA.js';
import { ProtocolIcon } from './chunk-Q3P6T5DK.js';
import { ProtocolBadge } from './chunk-OVXROGOV.js';
import { OKX_XBRIDGE, OKX_COMPONENTS, OKX_PACKED_PAYLOAD_KIND, fieldAtPath, OKX_FIELD_LABELS, ProtocolValue, SectionCard, FactGrid, ProtocolParamList, ProtocolDisclosure, decodedOrRaw, legField } from './chunk-IFYJS34L.js';
import { Boxes, Shuffle } from 'lucide-react';
import { MiddleEllipsis, CopyButton } from '@authe/ui-core';
import { jsxs, jsx, Fragment } from 'react/jsx-runtime';
import { MINIMUM_AMOUNT_DESCRIPTION } from '@authe/fe-commons/display';

function OkxPackedPayload({ display, raw }) {
  const label = typeof display === "string" && display ? display : "Packed payload";
  const hex = typeof raw === "string" ? raw : "";
  return /* @__PURE__ */ jsxs("span", { className: "inline-flex min-w-0 flex-wrap items-center gap-1.5 align-middle", children: [
    /* @__PURE__ */ jsxs("span", { className: "inline-flex items-center gap-1 rounded-sm bg-muted px-1.5 py-px text-2xs font-medium text-muted-foreground", children: [
      /* @__PURE__ */ jsx(Boxes, { className: "size-3", "aria-hidden": true }),
      label
    ] }),
    hex && /* @__PURE__ */ jsxs("span", { className: "inline-flex min-w-0 items-center gap-1 font-mono text-2xs text-muted-foreground", children: [
      /* @__PURE__ */ jsx(MiddleEllipsis, { value: hex, kind: "hash" }),
      /* @__PURE__ */ jsx(CopyButton, { value: hex })
    ] })
  ] });
}
function asRequests(value) {
  const v = decodedOrRaw(value);
  if (Array.isArray(v)) return v.filter((r) => r != null);
  return v != null ? [v] : [];
}
function swapFacts(swap) {
  const get = (...names) => legField(swap, ...names);
  const method = decodedOrRaw(get("name"));
  return [
    { label: OKX_FIELD_LABELS.swapMethod, value: typeof method === "string" ? method : null, when: typeof method === "string" && method },
    { label: OKX_FIELD_LABELS.fromToken, value: /* @__PURE__ */ jsx(ProtocolValue, { value: get("fromToken", "fromTokenAddress") }), when: get("fromToken", "fromTokenAddress") },
    { label: OKX_FIELD_LABELS.fromTokenAmount, value: /* @__PURE__ */ jsx(ProtocolValue, { value: get("fromTokenAmount") }), when: get("fromTokenAmount") != null },
    { label: OKX_FIELD_LABELS.toToken, value: /* @__PURE__ */ jsx(ProtocolValue, { value: get("toToken") }), when: get("toToken") },
    { label: OKX_FIELD_LABELS.receiver, value: /* @__PURE__ */ jsx(ProtocolValue, { value: get("receiver") }), when: get("receiver") }
  ];
}
function OkxBridgeRequest({ request, caption }) {
  const get = (...names) => legField(request, ...names);
  const flag = decodedOrRaw(get("flag"));
  const inbound = flag === 3 || flag === "3";
  const outbound = flag === 0 || flag === "0" || get("amountMinimum") != null;
  const token = get("token");
  const recipient = get("recipient");
  const destChain = get("destChain");
  const amount = get("amount");
  const minReturn = get("minReturnAmount");
  const fee = get("fee");
  const swap = get("swap");
  const destChainId = decodedOrRaw(legField(destChain, "chain_id"));
  const unrecognized = token === void 0 ? get("unrecognized") : void 0;
  if (token === void 0) {
    return /* @__PURE__ */ jsx(SubCard, { caption, children: /* @__PURE__ */ jsx(ProtocolValue, { value: unrecognized ?? request }) });
  }
  const facts = [
    { label: OKX_FIELD_LABELS.direction, value: inbound ? "Inbound redemption" : "Outbound request", when: inbound || outbound },
    { label: OKX_FIELD_LABELS.token, value: /* @__PURE__ */ jsx(ProtocolValue, { value: token }), when: token },
    {
      // The decoder uses the same wire field for two different meanings:
      // outbound minimum versus inbound redeemed input to the destination swap.
      label: inbound ? OKX_FIELD_LABELS.redeemedAmount : outbound ? OKX_FIELD_LABELS.minReturnAmount : OKX_FIELD_LABELS.requestAmount,
      value: /* @__PURE__ */ jsxs("span", { className: "inline-flex items-center gap-1", children: [
        /* @__PURE__ */ jsx(ProtocolValue, { value: minReturn }),
        outbound && !inbound && /* @__PURE__ */ jsx(
          "span",
          {
            className: "text-2xs uppercase tracking-wide text-muted-foreground",
            title: MINIMUM_AMOUNT_DESCRIPTION,
            children: "(min)"
          }
        )
      ] }),
      when: minReturn != null
    },
    { label: OKX_FIELD_LABELS.amount, value: /* @__PURE__ */ jsx(ProtocolValue, { value: amount }), when: amount != null },
    { label: OKX_FIELD_LABELS.recipient, value: /* @__PURE__ */ jsx(ProtocolValue, { value: recipient, chain: outbound && typeof destChainId === "string" ? destChainId : void 0 }), when: recipient },
    { label: OKX_FIELD_LABELS.destChain, value: /* @__PURE__ */ jsx(ProtocolValue, { value: destChain }), when: destChain },
    { label: OKX_FIELD_LABELS.fee, value: /* @__PURE__ */ jsx(ProtocolValue, { value: fee }), when: fee },
    { label: OKX_FIELD_LABELS.finalToken, value: /* @__PURE__ */ jsx(ProtocolValue, { value: get("finalToken") }), when: inbound && get("finalToken") != null }
  ];
  const swapFactList = swap != null ? swapFacts(swap) : [];
  return /* @__PURE__ */ jsxs(SubCard, { caption, children: [
    /* @__PURE__ */ jsx(FactGrid, { facts }),
    swapFactList.some((fact) => fact.when) && /* @__PURE__ */ jsx("div", { className: "mt-2 rounded-sm border border-border/50 px-2 py-1.5", children: /* @__PURE__ */ jsx(FactGrid, { facts: swapFactList }) })
  ] });
}
function OkxXBridgeSwap({ call, hint }) {
  const refId = fieldAtPath(call.params, "refId");
  const requests = asRequests(fieldAtPath(call.params, "requests", "request"));
  const count = requests.length;
  const title = /* @__PURE__ */ jsxs("span", { className: "inline-flex items-center gap-2", children: [
    /* @__PURE__ */ jsx(ProtocolIcon, { protocolId: OKX_XBRIDGE.protocolId, icon: Shuffle }),
    /* @__PURE__ */ jsx("span", { className: "text-sm font-semibold", children: hint.label ?? call.label ?? "Swap and bridge" })
  ] });
  const meta = /* @__PURE__ */ jsxs(Fragment, { children: [
    count > 0 && /* @__PURE__ */ jsxs("span", { className: "text-2xs text-muted-foreground", children: [
      count,
      " ",
      count === 1 ? "bridge request" : "bridge requests"
    ] }),
    /* @__PURE__ */ jsx(ProtocolBadge, { protocolId: OKX_XBRIDGE.protocolId })
  ] });
  const headerFacts = [
    { label: OKX_FIELD_LABELS.refId, value: /* @__PURE__ */ jsx(ProtocolValue, { value: refId }), when: refId != null }
  ];
  return /* @__PURE__ */ jsxs(SectionCard, { title, protocolId: OKX_XBRIDGE.protocolId, meta, children: [
    /* @__PURE__ */ jsx("p", { className: "mb-2 text-xs text-muted-foreground", children: "Decoded request values. Check transfer receipts for executed delivery and final proceeds." }),
    count > 0 ? /* @__PURE__ */ jsxs(Fragment, { children: [
      refId != null && /* @__PURE__ */ jsx(FactGrid, { facts: headerFacts }),
      /* @__PURE__ */ jsx("div", { className: "mt-2 space-y-2", children: requests.map((request, i) => /* @__PURE__ */ jsx(
        OkxBridgeRequest,
        {
          request,
          caption: count === 1 ? "Bridge request" : `Bridge request ${i + 1} of ${count}`
        },
        i
      )) })
    ] }) : (
      // The packed payload didn't unpack into the expected requests[] — show
      // the generic argument list rather than an empty card.
      /* @__PURE__ */ jsx(ProtocolParamList, { params: call.params, emptyLabel: "No decoded arguments" })
    ),
    /* @__PURE__ */ jsx(ProtocolDisclosure, { className: "mt-3 min-w-0", summary: "All call arguments", children: /* @__PURE__ */ jsx("div", { className: "pt-2", children: /* @__PURE__ */ jsx(ProtocolParamList, { params: call.params, emptyLabel: "No decoded arguments" }) }) })
  ] });
}

// src/protocols/okx_xbridge/index.ts
var PID = OKX_XBRIDGE.protocolId;
var OKX_XBRIDGE_HINTS = {
  valueHints: [
    { protocolId: PID, kind: OKX_PACKED_PAYLOAD_KIND, component: OKX_COMPONENTS.packedPayload, fields: [] }
  ],
  eventHints: [],
  callHints: [
    { protocolId: PID, call: "swapAndBridge", component: OKX_COMPONENTS.swap, summaryFields: [] },
    { protocolId: PID, call: "swapAndBridgeBatch", component: OKX_COMPONENTS.swap, summaryFields: [] }
  ]
};
function registerOkxXBridge(registry) {
  registry.registerCall(OKX_COMPONENTS.swap, OkxXBridgeSwap);
  registry.registerValue(OKX_COMPONENTS.packedPayload, OkxPackedPayload);
  registry.registerHints(OKX_XBRIDGE_HINTS);
  return registry;
}

export { OKX_XBRIDGE_HINTS, OkxPackedPayload, OkxXBridgeSwap, registerOkxXBridge };
//# sourceMappingURL=chunk-KYGXQ3IQ.js.map
//# sourceMappingURL=chunk-KYGXQ3IQ.js.map